/**
 * File: contractReceipts.ts
 * Path: packages/config/src/contracts/contractReceipts.ts
 * Description: Payments marked as received, and what is still owed.
 *
 * Staff tick each scheduled payment off by hand when the money lands
 * (contracts/{id}/receipts/{payment reference}); there is no bank link. One
 * receipt per payment, so its document id is the payment's reference. The
 * balance is worked out here so the contract page, the client page and the
 * tests agree.
 */

import { contractPaymentReferences } from "./contractSchedule";
import type { Contract, ContractPaymentReference } from "./contractTypes";

/** A payment the client has made, as recorded by staff. */
export interface PaymentReceipt {
  /** The payment's reference ("IDK-C012-02-DEP"); also the document id. */
  reference: string;
  /** What actually arrived, in rand. */
  amount: number;
  /** "2026-10-05", the day it arrived. */
  receivedOn: string;
  recordedBy: string;
  recordedAt: number | null;
  /**
   * True when no invoice existed yet, so the money waits as the client's
   * credit (not income) until the payment's invoice is made and uses it.
   */
  held?: boolean;
}

export interface PaymentRow extends ContractPaymentReference {
  receipt: PaymentReceipt | null;
}

export interface ContractBalance {
  rows: PaymentRow[];
  /** Everything the plan schedules. */
  total: number;
  /** Everything marked received. */
  paid: number;
  /** total less paid, never below zero. */
  owed: number;
  settled: boolean;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isReceiptDate = (value: string): boolean =>
  DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

type Raw = Record<string, unknown>;

/** A receipt from a stored document; null when it is not one. */
export const toPaymentReceipt = (
  id: string,
  data: Raw | null | undefined,
): PaymentReceipt | null => {
  const r = data ?? {};
  const amount = typeof r.amount === "number" ? r.amount : NaN;
  const receivedOn = typeof r.receivedOn === "string" ? r.receivedOn : "";
  if (!Number.isFinite(amount) || amount <= 0 || !isReceiptDate(receivedOn)) {
    return null;
  }
  const at = r.createdAt as { toMillis?: () => number } | number | undefined;
  return {
    reference: id,
    amount,
    receivedOn,
    recordedBy: typeof r.recordedBy === "string" ? r.recordedBy : "",
    recordedAt:
      typeof at === "number"
        ? at
        : typeof at?.toMillis === "function"
          ? at.toMillis()
          : null,
    ...(r.held === true ? { held: true } : {}),
  };
};

export const contractBalance = (
  contract: Pick<Contract, "reference" | "content">,
  receipts: PaymentReceipt[],
): ContractBalance => {
  const byReference = new Map(receipts.map((r) => [r.reference, r]));
  const rows = contractPaymentReferences(
    contract.reference,
    contract.content,
  ).map((p) => ({ ...p, receipt: byReference.get(p.reference) ?? null }));
  const cents = (n: number) => Math.round(n * 100);
  const total = rows.reduce((sum, r) => sum + cents(r.amount), 0);
  const paid = rows.reduce((sum, r) => sum + cents(r.receipt?.amount ?? 0), 0);
  return {
    rows,
    total: total / 100,
    paid: paid / 100,
    owed: Math.max(0, total - paid) / 100,
    settled: rows.length > 0 && total > 0 && paid >= total,
  };
};
